import { useNavigator } from '@repo/toolkit/use-navigator'
import { FormIcon } from '@repo/ui/icons'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { toast } from 'sonner'

import { createEnrollment } from '../../../api/enrollments/create-enrollment'
import { createEnrollmentProposition } from '../../../api/enrollments/create-enrollment-proposition'
import { createOtoIndividualProposition } from '../../../api/enrollments/create-oto-individual-proposition'
import { getJourney } from '../../../api/products/get-journey'
import UserNavBar from '../../../components/elements/nav-bar'
import EnrollmentQuotationForm from '../../../components/pages/enrollments/enrollment-quotation-form'
import EnrollmentPropositionFamilyArrangementModal from '../../../components/pages/enrollments/enrolment-create-proposition-steps/enrollment-proposition-family-arrangement-modal'
import {
  mapEnrollmentPropositionInputToRequest,
  mapEnrollmentToEnrollmentQuotationInput
} from '../../../components/pages/enrollments/utils/enrollments-utils'
import { Paths } from '../../../lib/constants/path'
import { ApplicationStatusEnum } from '../../../lib/types/enums/enrollment/application-status.enum'
import { FamilyArrangementEnum } from '../../../lib/types/enums/ol/family-arrangement.enum'
import { EnrollmentDuplicationType } from '../../../lib/types/fields/enrollments/enrollment'
import { EnrollmentQuotationInput } from '../../../lib/types/models/enrollment/enrollment-input'
import { mapOtoIndividualPropositionInputToRequest } from '../../../lib/types/models/enrollment-proposition/oto-individual-proposition-input'
import { twoButtonsConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'

const EnrollmentQuotationCreatePage = () => {
  const [hasJourney, setHasJourney] = useState(false)
  const { back } = useNavigator()
  const state = useLocation().state as EnrollmentDuplicationType
  const queryClient = useQueryClient()
  const [valuesToConfirm, setValuesToConfirm] = useState<EnrollmentQuotationInput>()

  const { mutateAsync: createEnrollmentMutation } = useMutation({
    mutationFn: createEnrollment,
    onError: showAxiosError
  })

  const { mutateAsync: createEnrollmentPropositionMutation } = useMutation({
    mutationFn: createEnrollmentProposition,
    onError: showAxiosError
  })

  const { mutateAsync: createOtoIndividualPropositionMutation } = useMutation({
    mutationFn: createOtoIndividualProposition,
    onError: showAxiosError
  })

  // Create the enrollment first and then the proposition
  const handleSubmit = async (values: EnrollmentQuotationInput) => {
    const { proposition, otoIndividualProposition, ...enrollmentData } = values
    const enrollment = await createEnrollmentMutation({
      ...enrollmentData,

      declaredFriends: proposition?.declaredFriends ?? [],
      status: ApplicationStatusEnum.QUOTE
    })

    if (otoIndividualProposition)
      await createOtoIndividualPropositionMutation({
        id: enrollment.id,
        ...mapOtoIndividualPropositionInputToRequest(otoIndividualProposition)
      })
    else if (proposition)
      await createEnrollmentPropositionMutation({
        id: enrollment.id,
        ...mapEnrollmentPropositionInputToRequest(proposition)
      })

    // the devis is only created once its proposition is saved, whichever kind it is
    toast.success('Devis créé avec succès')
    return enrollment
  }

  const createAndLeave = async (values: EnrollmentQuotationInput) => {
    const res = await handleSubmit(values)
    back(Paths.ENROLLMENT_QUOTATION_UPDATE_PAGE.replace(':id', res.id.toString()))
  }

  // a stay offering both FA1E and FA2E needs the family arrangement chosen before creating (e.g. duplicate of a legacy request without one)
  const handleConfirm = async (values: EnrollmentQuotationInput) => {
    if (
      values.proposition?.journey &&
      !values.otoIndividualProposition &&
      !values.proposition.familyArrangement
    ) {
      const { id } = values.proposition.journey
      const stay = await queryClient.fetchQuery({
        queryKey: ['journey', id],
        queryFn: () => getJourney(id)
      })
      if (stay.fa1eIncluded && stay.fa2eIncluded) {
        setValuesToConfirm(values)
        return
      }
    }
    await createAndLeave(values)
  }

  return (
    <div className="flex flex-col gap-4">
      <UserNavBar icon={FormIcon} title="Créer un nouveau devis" />
      <EnrollmentQuotationForm
        mode="create"
        defaultValues={state?.id ? mapEnrollmentToEnrollmentQuotationInput(state) : undefined}
        onJourneyChange={() => setHasJourney(true)}
        buttonsConfig={twoButtonsConfig({
          confirmText: hasJourney ? 'Envoyer le devis' : 'Valider',
          onConfirm: handleConfirm,
          onCancel: () => back()
        })}
      />

      <EnrollmentPropositionFamilyArrangementModal
        isOpen={!!valuesToConfirm}
        onClose={() => setValuesToConfirm(undefined)}
        onSubmit={async (familyArrangement: FamilyArrangementEnum) => {
          if (!valuesToConfirm?.proposition) return
          const values = {
            ...valuesToConfirm,
            proposition: { ...valuesToConfirm.proposition, familyArrangement }
          }
          setValuesToConfirm(undefined)
          await createAndLeave(values)
        }}
      />
    </div>
  )
}

export default EnrollmentQuotationCreatePage
